import { Button, IconButton, Typography } from '@material-ui/core';
import MuiDialog from '@material-ui/core/Dialog';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import React, { useState } from 'react';
import { ReactElement } from 'react';
import { DialogWidth } from '../types';
import ClosureErrorIcon from './Icons/ClosureErrorIcon';
import { ValidateIcon } from './Icons';

const useStyles = makeStyles(theme => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      padding: '24px',
      paddingTop: '48px',
      paddingLeft: '48px',
    },
    '& .MuiDialogTitle-root': {
      padding: '0px',
    },
  },

  wrapper: {
    display: 'flex',
    flexFlow: 'column',
    textAlign: 'left',
    padding: '0px',
    paddingTop: '24px',
  },

  footer: {
    display: 'flex',
    flexFlow: 'column',
    textAlign: 'left',
    padding: '0px',
  },

  title: {
    textAlign: 'left',
    padding: '0px 0px',
    whiteSpace: 'pre-line',
    lineHeight: '115%',
    fontSize: '24px',
    fontWeight: 600,
  },

  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey['900'],
  },
  bottomButton: {
    textAlign: 'right',
  },
  closeIcon: {
    width: '16px',
  },
  continueIcon: {
    width: '16px',
  },
  continueButton: {
    backgroundColor: '#2F3741',
    color: '#FFFFFF',
    marginLeft: theme.spacing(2),

    '&:hover': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
    '&:active': {
      color: '#2F3741',
      '& *': {
        color: '#2F3741',
      },
    },
  },
  content: {
    marginBottom: '32px',
    marginRight: '32px',

    '& p': {
      fontSize: 16,
      lineHeight: '24px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& p span': {
      color: '#FF9500',
    },
  },
}));

interface Props {
  isOpen?: boolean;
  renderButton?: any;
  renderContent: () => ReactElement;
  renderFooter?: () => ReactElement;
  title?: string | ReactElement;
  closable?: boolean;
  onClose?: () => void;
  width?: DialogWidth;
  genericFooter?: boolean;
  closeButtonLabelFooter?: string;
  continueButtonLabelFooter?: string;
  handleDialog?: () => void;
}

const NewDialog: React.FC<Props> = ({
  isOpen,
  renderButton,
  renderContent,
  renderFooter,
  title,
  closable = true,
  onClose = () => null,
  width = DialogWidth.DEFAULT,
  genericFooter = false,
  closeButtonLabelFooter,
  continueButtonLabelFooter,
  handleDialog,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const classes = useStyles();

  const handleClose = () => {
    if (closable) {
      setOpen(false);
      onClose();
    }
  };

  const disableBackDropClick = (closeAction: () => void) => (_: {}, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason !== 'backdropClick') {
      closeAction();
    }
  };

  return (
    <>
      {renderButton && renderButton(() => setOpen(true))}
      <MuiDialog
        className={classes.dialogRoot}
        open={open || !!isOpen}
        onClose={disableBackDropClick(handleClose)}
        maxWidth={width}
        fullWidth={true}
        {...props}
      >
        <MuiDialogTitle disableTypography>
          {closable && (
            <IconButton aria-label="close" className={classes.closeButton} onClick={handleClose}>
              <Close />
            </IconButton>
          )}
          <div>
            {title && (
              <Typography variant="h2" className={classes.title}>
                {title}
              </Typography>
            )}
          </div>
        </MuiDialogTitle>
        <MuiDialogContent className={classes.wrapper}>{renderContent()}</MuiDialogContent>
        {renderFooter && <MuiDialogContent className={classes.footer}>{renderFooter()}</MuiDialogContent>}
        {genericFooter && (
          <MuiDialogContent className={classes.footer}>
            <div className={classes.bottomButton}>
              <Button
                variant="outlined"
                startIcon={<ClosureErrorIcon className={classes.closeIcon} />}
                onClick={handleClose}
              >
                {closeButtonLabelFooter}
              </Button>
              <Button
                className={classes.continueButton}
                variant="outlined"
                endIcon={<ValidateIcon className={classes.continueIcon} />}
                onClick={handleDialog}
              >
                {continueButtonLabelFooter}
              </Button>
            </div>
          </MuiDialogContent>
        )}
      </MuiDialog>
    </>
  );
};
export default NewDialog;
